/**
 * Canonical role names (exactly as seeded in the `roles` table) and the role
 * groups used for access checks. Mirrors backend/app/Support/Roles.php - keep
 * the two in sync. Matching is case-insensitive; aliases are NOT accepted for
 * access checks (use normalizeRole only for external/legacy input).
 */
export const ROLES = Object.freeze({
  CHAIRMAN: "Chairman",
  MD: "MD",
  MANAGER: "Manager",
  INVESTOR: "Investor",
  SHARE_HOLDER: "Share Holder",
  DIRECTOR: "Director",
  USER: "User",
  ADMIN: "Admin",
  SUPERADMIN: "Superadmin",
  EXECUTIVE_DIRECTOR: "Executive Director",
  FINANCE_DIRECTOR: "Finance Director",
  GM: "GM",
  ASSISTANT_GENERAL_MANAGER: "Assistant General Manager",
  MARKETING_MANAGER: "Marketing Manager",
  MARKETING_OFFICER: "Marketing Officer",
});

export const ALL_ROLES = Object.freeze(Object.values(ROLES));

/** Admin-level roles (Superadmin can do everything Admin can, plus the SUPERADMIN_ONLY actions). */
export const ADMINS = Object.freeze([ROLES.ADMIN, ROLES.SUPERADMIN]);

/** Who may approve or reject bookings (/manager/bookings). */
export const BOOKING_APPROVERS = Object.freeze([
  ROLES.ADMIN,
  ROLES.SUPERADMIN,
  ROLES.MANAGER,
]);

/**
 * Who may open the read-only dashboard (/dashboard) and reports (/reports/*), including their
 * PDF and CSV exports. Managers can view but never write. Mirrors Roles::REPORT_VIEWERS.
 */
export const REPORT_VIEWERS = Object.freeze([
  ROLES.ADMIN,
  ROLES.SUPERADMIN,
  ROLES.MANAGER,
]);

/**
 * Who may open the pending-payments and overdue-payments reports (and the /reports hub, which then
 * lists only those two): the report viewers plus the Finance Director. Mirrors Roles::PAYMENT_REPORT_VIEWERS.
 */
export const PAYMENT_REPORT_VIEWERS = Object.freeze([
  ...REPORT_VIEWERS,
  ROLES.FINANCE_DIRECTOR,
]);

/**
 * Who may file a refund request and read the company's requests (/refund-requests). Only a Superadmin
 * approves or rejects them. Mirrors Roles::REFUND_REQUESTERS.
 */
export const REFUND_REQUESTERS = Object.freeze([
  ROLES.FINANCE_DIRECTOR,
  ROLES.ADMIN,
  ROLES.SUPERADMIN,
]);

/**
 * Who gets the shared Leadership dashboard (/leadership): one page, with the cards each role sees decided
 * by the API (GET /api/leadership/overview). Mirrors Roles::LEADERSHIP_VIEWERS. Finance Director and
 * Marketing Officer keep their own work dashboards and are not part of it.
 */
export const LEADERSHIP_VIEWERS = Object.freeze([
  ROLES.CHAIRMAN,
  ROLES.MD,
  ROLES.EXECUTIVE_DIRECTOR,
  ROLES.INVESTOR,
]);

/** LEADERSHIP_VIEWERS plus the admins, who may open the page and preview every card. */
export const LEADERSHIP_DASHBOARD_ACCESS = Object.freeze([...LEADERSHIP_VIEWERS, ...ADMINS]);

/** Who may record a sale (Make a Sale). */
export const SALES_RECORDERS = Object.freeze([
  ROLES.ADMIN,
  ROLES.SUPERADMIN,
  ROLES.MD,
  ROLES.MARKETING_MANAGER,
]);

/**
 * Who may open Make a Sale and call POST /api/sales: the sales recorders plus Marketing Officers.
 * A Marketing Officer sells as themselves, to a new customer or one found by exact email / phone.
 * Mirrors Roles::SALE_CREATORS.
 */
export const SALE_CREATORS = Object.freeze([...SALES_RECORDERS, ROLES.MARKETING_OFFICER]);

/** Finance staff (late fees, installments), including admins. */
export const FINANCE_STAFF_WITH_ADMINS = Object.freeze([
  ROLES.ADMIN,
  ROLES.SUPERADMIN,
  ROLES.FINANCE_DIRECTOR,
]);

/** Admins and managers (reports menu, property management). */
export const ADMINS_AND_MANAGERS = Object.freeze([
  ROLES.ADMIN,
  ROLES.SUPERADMIN,
  ROLES.MANAGER,
]);

/** Roles that can sit in the role-approval chain. */
export const APPROVER_ROLES = Object.freeze([
  ROLES.MANAGER,
  ROLES.MD,
  ROLES.FINANCE_DIRECTOR,
  ROLES.CHAIRMAN,
  ROLES.EXECUTIVE_DIRECTOR,
]);

/** Sales staff roles (marketing team). */
export const SALES_STAFF = Object.freeze([
  ROLES.MARKETING_OFFICER,
  ROLES.MARKETING_MANAGER,
]);

/** Legacy / external names mapped to canonical roles (keys are lowercase). */
export const LEGACY_ROLE_ALIASES = Object.freeze({
  cfo: ROLES.FINANCE_DIRECTOR,
  "iconic cfo": ROLES.FINANCE_DIRECTOR,
  "iconic unity cfo": ROLES.FINANCE_DIRECTOR,
  ed: ROLES.EXECUTIVE_DIRECTOR,
  mo: ROLES.MARKETING_OFFICER,
  agent: ROLES.MARKETING_OFFICER,
  mm: ROLES.MARKETING_MANAGER,
  managing_director: ROLES.MD,
  "managing director": ROLES.MD,
  "iconic md": ROLES.MD,
  "iconic unity md": ROLES.MD,
  "iconic chairman": ROLES.CHAIRMAN,
  "iconic unity chairman": ROLES.CHAIRMAN,
});

/** Lowercase, trimmed comparison key. Use this when comparing role names. */
export const roleKey = (name) => (name || "").toString().trim().toLowerCase();

/**
 * Map a role name (canonical in any casing, or a legacy alias) to its canonical
 * name. Unknown names are returned trimmed and unchanged.
 */
export function normalizeRole(name) {
  const trimmed = (name || "").toString().trim();
  const key = trimmed.toLowerCase();
  if (LEGACY_ROLE_ALIASES[key]) return LEGACY_ROLE_ALIASES[key];
  return ALL_ROLES.find((role) => role.toLowerCase() === key) || trimmed;
}

/**
 * Superadmin = everything Admin has, plus the actions only a Superadmin may do (mirrors the
 * `superadmin` middleware in backend/routes/api.php): refunds, commission rates and held
 * commissions, system settings, approval toggle / approval-chain settings, role create/delete,
 * and granting or removing the Admin/Superadmin role.
 */
export const SUPERADMIN_ONLY = Object.freeze([ROLES.SUPERADMIN]);

/** Roles only a Superadmin may grant or remove. */
export const PRIVILEGED_ROLES = Object.freeze([ROLES.ADMIN, ROLES.SUPERADMIN]);

/**
 * Roles nobody can REQUEST (Role Selection, role-apply). A Superadmin grants them from User Roles.
 * Mirrors Roles::NOT_REQUESTABLE. Not the same as the approval-chain rule below.
 */
export const NOT_REQUESTABLE_ROLES = Object.freeze([ROLES.ADMIN, ROLES.SUPERADMIN]);

/**
 * Roles no longer in use: hidden from User Roles and Manage Roles, never newly granted (the API refuses
 * it). Greenland runs with a single Superadmin, so Admin is retired. Mirrors Roles::RETIRED.
 */
export const RETIRED_ROLES = Object.freeze([ROLES.ADMIN]);

/**
 * Roles that cannot be configured as approval-chain APPROVERS: Superadmin (not a chain role) and the
 * retired roles. Mirrors Roles::NOT_CHAIN_APPROVERS.
 */
export const NOT_CHAIN_APPROVER_ROLES = Object.freeze([ROLES.SUPERADMIN, ...RETIRED_ROLES]);

export const SUPERADMIN_ONLY_LABEL = "Superadmin only";
export const SUPERADMIN_DENIED_MESSAGE = "Only a Superadmin can do this.";
